import { useLayoutEffect, useRef, useState } from 'react';

import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { isMobile } from '@/consts/device';
import { getContext } from '@/context';
import { setAppStore } from '@/store';
import { Screen } from '@/types';
import { isHostEmbed } from '@/utils/environment/surface';
import { markHostNavigation } from '@/utils/url/goBackToHost';

import presetHandler from './handlers/presetHandler';
import getPresetSlugFromHostRoute from './hostRoute';
import { getDeeplinkSnapshot } from './snapshot';
import { DeeplinkKind } from './types';

/** Whether the path names a preset this hook is the one to open. */
const hasHostRoutePreset = (): boolean =>
  isHostEmbed() &&
  // A param deeplink outranks the path: the router is already dispatching it.
  getDeeplinkSnapshot().kind === DeeplinkKind.None &&
  Boolean(getPresetSlugFromHostRoute());

/**
 * Opens the preset named by the *host page's* path, and re-opens it whenever
 * the host navigates.
 *
 * Deeplinks are query params read once at boot, which is enough when this app
 * owns its URL. Embedded it does not: gen.ai carries the preset in the path
 * (`/professional-profile/executive-portrait`) and keeps one instance of this
 * app alive across its client-side navigations — so there is no param to read
 * and no remount that would re-read one. Without this the embed always opens on
 * its default screen, whichever preset was clicked.
 *
 * It routes through `presetHandler`, the same handler a `presetId` deeplink
 * uses, so a path-borne preset and a param-borne one open identically —
 * including the mobile feed.
 *
 * `isPending` mirrors the deeplink router's: true while the *first* dispatch is
 * in flight, so nothing paints before it lands. Mobile needs that — the handler
 * may resolve into the preset feed, and the shell would otherwise paint the
 * library first and visibly replace it a moment later. Later navigations do not
 * block: the app is on screen by then and the preset arrives under its own
 * loading state.
 */
const useHostRoutePreset = (): { isPending: boolean } => {
  // Blocking is a mobile concern only (see above), and only when there is a
  // preset in the path to wait for.
  const [isPending, setIsPending] = useState<boolean>(
    () => isMobile && hasHostRoutePreset()
  );
  // The slug whose dispatch is the current source of truth, so an unchanged
  // route (the host pushing an unrelated navigation) costs nothing.
  const activeSlugRef = useRef<string | null>(null);

  // Layout, not passive: on desktop `presetHandler` sets the Create screen
  // synchronously before its first await, and running it before paint is what
  // keeps that screen from being the second thing the user sees.
  useLayoutEffect(() => {
    if (!isHostEmbed()) return undefined;

    let disposed = false;
    // Dispatches run one after another rather than concurrently: each writes
    // the preset it resolved straight into the store, so two overlapping
    // navigations could otherwise finish out of order and leave the store on
    // the one the user left.
    let queue: Promise<void> = Promise.resolve();

    const openPresetForHostRoute = () => {
      const slug = getPresetSlugFromHostRoute();

      if (!slug) {
        // The host left the preset for a page of its own. Close what the route
        // opened: the feed is a *global* overlay — the design system portals it
        // to the document body, so it outlives the host hiding the container
        // this app was mounted into, and would sit over the host's next page
        // swallowing its clicks. Only ever closes this hook's own doing, so a
        // param deeplink's screen is never pulled out from under it.
        if (activeSlugRef.current === null) return;
        activeSlugRef.current = null;
        setAppStore({ screen: Screen.Library });
        return;
      }

      if (slug === activeSlugRef.current) return;
      activeSlugRef.current = slug;

      queue = queue
        .then(() => {
          if (disposed) return undefined;
          return presetHandler({ kind: DeeplinkKind.Preset, presetId: slug });
        })
        // Caught per link, not once around the chain: a rejection that escaped
        // the handler would otherwise poison the queue and swallow every
        // navigation after it.
        .catch((error) => {
          errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
        })
        .finally(() => {
          // Whatever the outcome — opened, fell back to the library, failed —
          // the app has to become visible again.
          if (!disposed) setIsPending(false);
        });
    };

    // The page the host loaded, against which every later read is a
    // navigation. Compared by slug, not by ordering: the subscription replays
    // its current value on subscribe, and whether that lands before or after
    // this effect finishes is the SDK's business, not something to depend on.
    const bootSlug = getPresetSlugFromHostRoute();

    // The slug is recorded even when a param deeplink wins, or the location
    // subscription below would immediately dispatch the path preset over it.
    if (hasHostRoutePreset()) {
      openPresetForHostRoute();
    } else {
      activeSlugRef.current = bootSlug;
      setIsPending(false);
    }

    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = getContext().general.location.subscribe(
        () => {
          if (disposed) return;
          // Off the page it loaded, so there is now a host page behind us —
          // which is what tells the feed's back button that handing the move to
          // the host history will land somewhere the user has actually been.
          if (getPresetSlugFromHostRoute() !== bootSlug) markHostNavigation();
          openPresetForHostRoute();
        },
        { needPrevious: true }
      );
    } catch {
      /* context not ready — the initial dispatch already used the browser path */
    }

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, []);

  return { isPending };
};

export { useHostRoutePreset };
